import { Row, Col, Input, Typography } from "antd"
import {
  MobileOutlined,
  WifiOutlined,
  WhatsAppOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  WalletOutlined,
} from "@ant-design/icons"
import { Link, useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import "./MobileQuickActions.css"

const { Text } = Typography

/**
 * The mobile / tablet home screen.
 *
 * On a phone the page used to open with an animated background, a headline
 * rendered as ~48 individual spans, six payment logos and a search box — the
 * only real navigation, the provider tiles, sat below the fold. And because the
 * header's network nav collapses into a hamburger under 992px, a mobile visitor
 * had no visible way to reach a network at all without scrolling.
 *
 * The WhatsApp bot has the opposite shape and works: a short menu that reaches
 * Airtime, Data or ZESA in one tap. These labels are taken verbatim from the
 * live bot menu (node main_list-gHpFr) so the two surfaces read identically.
 *
 * Visibility is CSS-only (MobileQuickActions.css, max-width 991.98px), never a
 * JS breakpoint: useDeviceDetection initialises to false and flips after mount,
 * which would paint the desktop hero for a frame first. CSS applies before the
 * first paint, and it keeps the prerendered snapshot consistent for crawlers.
 */

// Category cards point at /shop/<type>, which lists that category across EVERY
// network. The taxonomy is stored network-first, so these have no single
// section of their own — the backend composes them (see getCategoryItems).
const ACTIONS = [
  { key: 'airtime',  label: 'Airtime, Voice & SMS', hint: 'All networks', to: '/shop/airtime',  Icon: MobileOutlined },
  { key: 'data',     label: 'Data & Social Media',  hint: 'Bundles',      to: '/shop/data',     Icon: WifiOutlined },
  { key: 'whatsapp', label: 'WhatsApp Bundles',     hint: 'Stay online',  to: '/shop/whatsapp', Icon: WhatsAppOutlined },
  { key: 'zesa',     label: 'ZESA Tokens',          hint: 'Electricity',  to: '/shop/zesa',     Icon: ThunderboltOutlined },
  { key: 'track',    label: 'Track Order',          hint: 'Where is it?', to: '/track-order',   Icon: FileTextOutlined },
  { key: 'wallet',   label: 'Wallet & Top Up',      hint: 'Your balance', to: '/account/dashboard', Icon: WalletOutlined },
]

const MobileQuickActions = () => {
  const navigate = useNavigate()

  const onSearch = (value) => {
    // Was navigating to "/products/search?q=...", which is the BACKEND api
    // endpoint (the same one HomePage's own desktop dropdown calls via
    // apiRequest), not a frontend route — so every mobile search 404'd.
    // /search is the real results page.
    const q = String(value || '').trim()
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`)
  }

  return (
    <section className="mobile-quick-actions" aria-label="Quick actions">
      <Input.Search
        placeholder="Search airtime, data, ZESA…"
        size="large"
        allowClear
        enterButton
        onSearch={onSearch}
        className="mqa-search"
        // Prevents iOS Safari zooming the whole page on focus, which is the
        // classic tell that a site is not built for the phone.
        inputMode="search"
      />

      <Row gutter={[14, 14]} className="mqa-grid">
        {ACTIONS.map(({ key, label, hint, to, Icon }) => (
          // 2-up on phones, 3-up from tablets. Deliberately not 4-up: the label
          // is a full phrase, and truncating it is what makes a grid feel cheap.
          <Col xs={12} sm={8} key={key}>
            <motion.div whileTap={{ scale: 0.97 }} transition={{ duration: 0.12 }}>
              {/* The whole card is the link — a button nested inside an anchor
                  is invalid markup and gives one action two hit areas. */}
              <Link to={to} className="mqa-card">
                <span className="mqa-icon" aria-hidden="true"><Icon /></span>
                <Text className="mqa-label">{label}</Text>
                <Text className="mqa-hint">{hint}</Text>
              </Link>
            </motion.div>
          </Col>
        ))}
      </Row>
    </section>
  )
}

export default MobileQuickActions
